import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Eye, EyeOff, ArrowLeft, Moon, Sun } from "lucide-react";
import { formatError } from "../lib/api";

export default function StudentLogin() {
  const { login, user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState("");

  useEffect(() => {
    window.history.replaceState({ fromApp: true }, '', '/');
    window.history.pushState({ fromApp: true }, '', '/student/login');
  }, []);

  useEffect(() => {
    if (user?.role === "student") navigate("/student/dashboard");
    else if (user?.role === "admin") navigate("/recruiter/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorType("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role === "student") navigate("/student/dashboard");
      else if (u.role === "admin") navigate("/recruiter/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setErrorType("not_found");
        setError("");
      } else if (status === 401) {
        setErrorType("wrong_password");
        setError("");
      } else {
        setErrorType("generic");
        setError(formatError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const pageBg = isDark ? '#0a0a0f' : '#f8f9fa';
  const cardBg = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)';
  const cardShadow = isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.06)';
  const textPrimary = isDark ? '#ffffff' : '#0a0a0f';
  const textSecondary = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const textMuted = isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8';
  const inputStyle = isDark
    ? { background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }
    : { background: 'rgba(0,0,0,0.04)', borderColor: 'rgba(0,0,0,0.1)', color: '#0a0a0f' };
  const accent = '#059669';
  const accentDark = '#047857';
  const glowColor = isDark ? 'rgba(5,150,105,0.12)' : 'rgba(249,115,22,0.06)';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative" style={{ background: pageBg }}>
      {/* Subtle ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${glowColor}, transparent)` }} />
      </div>

      {/* Theme toggle */}
      <button onClick={e => toggleTheme(e)} aria-label="Toggle theme"
        style={{ position: 'absolute', top: '16px', right: '16px', width: '34px', height: '34px', borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSecondary, zIndex: 10 }}>
        {isDark ? <Sun size={14} /> : <Moon size={14} />}
      </button>

      <div className="w-full max-w-sm relative">
        {/* Back to home */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mb-6 text-sm font-medium transition-colors"
          style={{ color: textSecondary, textDecoration: 'none' }}
        >
          <ArrowLeft size={15} /> Back to Home
        </Link>

        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2" style={{ textDecoration: 'none' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})` }}>
              <span className="text-white font-bold text-sm">W</span>
            </div>
            <span className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>WonStepCareer</span>
          </Link>
        </div>

        <Card className="shadow-2xl" style={{ background: cardBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: cardBorder, boxShadow: cardShadow }}>
          <CardHeader>
            <CardTitle className="text-xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Welcome back</CardTitle>
            <CardDescription style={{ color: textSecondary }}>Sign in to your student account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-sm" style={{ color: textSecondary }}>Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@college.edu"
                  required
                  className="mt-1 placeholder:text-gray-400"
                  style={inputStyle}
                  data-testid="login-email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-sm" style={{ color: textSecondary }}>Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Your password"
                    required
                    className="pr-10 placeholder:text-gray-400"
                    style={inputStyle}
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: textMuted }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {errorType === "not_found" && (
                <p className="text-sm text-red-500 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="login-error">
                  No account found with this email.{" "}
                  <Link to="/student/signup" className="font-medium underline" style={{ color: accent }}>Sign up for free!</Link>
                </p>
              )}
              {errorType === "wrong_password" && (
                <p className="text-sm text-red-500 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="login-error">
                  Incorrect password. Please try again.
                </p>
              )}
              {errorType === "generic" && error && (
                <p className="text-sm text-red-500 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="login-error">{error}</p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full font-semibold"
                style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white', boxShadow: `0 2px 12px rgba(5,150,105,0.3)` }}
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>

              <p className="text-center text-sm" style={{ color: textSecondary }}>
                Don't have an account?{" "}
                <Link to="/student/signup" className="font-medium hover:underline" style={{ color: accent }} data-testid="login-signup-link">
                  Sign up free
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>

        <p className="text-center mt-4 text-sm" style={{ color: textMuted }}>
          Are you a recruiter?{" "}
          <Link to="/recruiter/login" className="font-medium" style={{ color: textSecondary }}>Recruiter Login</Link>
        </p>
      </div>
    </div>
  );
}
